/*
 * Dayvee – Gutschein-Menge im Warenkorb
 *
 * Ziel:
 * - Gutscheinprodukt wird auf der Produktseite bewusst immer einzeln hinzugefügt.
 * - Im Cart Drawer und auf der Warenkorbseite kann die Menge eines Gutschein-
 *   Warenkorbpostens nicht nachträglich per +/- geändert werden.
 * - Mehrere Gutscheine entstehen durch erneutes Hinzufügen.
 * - Unterschiedliche Empfängerdaten bleiben dadurch als getrennte
 *   Warenkorbpositionen erhalten.
 * - Bei einem reinen Gutschein-Warenkorb werden MwSt.-Hinweis und Versandkosten
 *   auf Warenkorbseite und im Cart Drawer korrekt ausgeblendet.
 * - Für die 69-€-Versandfreigrenze zählt nur versandpflichtiger Warenwert;
 *   Geschenkgutscheine lösen keinen kostenlosen Versand für physische Ware aus.
 */

(function () {
  'use strict';

  var GIFT_CARD_HANDLE = 'geschenkgutschein';
  var initialized = false;
  var scheduled = false;
  var cartSummaryTimer = null;
  var cartSummaryBusy = false;
  var cartSummaryQueued = false;

  function normalizePath(value) {
    try {
      return new URL(value, window.location.origin).pathname.replace(/\/+$/, '');
    } catch (error) {
      return String(value || '')
        .split('?')[0]
        .split('#')[0]
        .replace(/\/+$/, '');
    }
  }

  function isGiftCardUrl(value) {
    return normalizePath(value).endsWith('/products/' + GIFT_CARD_HANDLE);
  }

  function getProductLink(row) {
    return row.querySelector(
      '.cart-item__name[href], .dayvee-cart__product-title[href]'
    );
  }

  function isGiftCardRow(row) {
    var link = getProductLink(row);
    return !!(link && isGiftCardUrl(link.getAttribute('href')));
  }

  function getQuantity(row) {
    var input = row.querySelector(
      'input.quantity__input, input[id^="Quantity-"], input[id^="Drawer-quantity-"], .dayvee-cart__qty-input'
    );

    var value = input ? parseInt(input.value, 10) : 1;
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  function createStaticQuantity(quantity) {
    var node = document.createElement('span');
    node.className = 'dayvee-gift-card-cart-quantity';
    node.setAttribute('aria-label', 'Anzahl Gutscheine: ' + quantity);
    node.textContent = quantity + '×';
    return node;
  }

  function lockDawnQuantity(row, quantity) {
    var quantityInput = row.querySelector(
      '.cart-item__quantity-wrapper quantity-input'
    );

    if (!quantityInput) return false;

    quantityInput.replaceWith(createStaticQuantity(quantity));
    return true;
  }

  function lockGiftCardQuantity(row) {
    if (!isGiftCardRow(row)) return;
    if (row.dataset.dayveeGiftCardQuantityLocked === 'true') return;

    var quantityValue = getQuantity(row);
    var changed = false;

    var dayveeQuantity = row.querySelector('.dayvee-cart__quantity');
    if (dayveeQuantity) {
      dayveeQuantity.replaceWith(createStaticQuantity(quantityValue));
      changed = true;
    } else {
      changed = lockDawnQuantity(row, quantityValue);
    }

    if (changed) {
      row.dataset.dayveeGiftCardQuantityLocked = 'true';
    }
  }

  function isGiftCardItemData(item) {
    if (!item) return false;

    if (String(item.handle || '').toLowerCase() === GIFT_CARD_HANDLE) {
      return true;
    }

    return isGiftCardUrl(item.url || '');
  }

  function itemRequiresShipping(item) {
    if (!item) return false;

    if (typeof item.requires_shipping === 'boolean') {
      return item.requires_shipping;
    }

    return !isGiftCardItemData(item);
  }

  function getLinePrice(item) {
    if (!item) return 0;

    var value =
      item.final_line_price != null
        ? item.final_line_price
        : item.line_price != null
          ? item.line_price
          : 0;

    value = Number(value);
    return Number.isFinite(value) ? value : 0;
  }

  function getCartFacts(cartData) {
    var items = Array.isArray(cartData && cartData.items)
      ? cartData.items
      : [];

    var shippableSubtotal = items.reduce(function (sum, item) {
      return itemRequiresShipping(item)
        ? sum + getLinePrice(item)
        : sum;
    }, 0);

    return {
      items: items,
      giftCardOnly:
        items.length > 0 &&
        items.every(isGiftCardItemData),
      hasShippableItems:
        items.some(itemRequiresShipping),
      shippableSubtotal: shippableSubtotal
    };
  }

  function formatMoney(cents, currency) {
    var amount = (Number(cents) || 0) / 100;

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'de-DE',
        {
          style: 'currency',
          currency: currency || 'EUR'
        }
      ).format(amount);
    } catch (error) {
      return amount.toFixed(2).replace('.', ',') + ' €';
    }
  }

  function setTextIfChanged(node, value) {
    if (!node) return;

    var next = String(value);
    if (node.textContent.trim() !== next) {
      node.textContent = next;
    }
  }

  function setHiddenIfChanged(node, hidden) {
    if (!node) return;

    if (hidden) {
      if (!node.hasAttribute('hidden')) {
        node.setAttribute('hidden', '');
      }
    } else if (node.hasAttribute('hidden')) {
      node.removeAttribute('hidden');
    }
  }

  function findSummaryRow(root, label) {
    if (!root) return null;

    return Array.from(
      root.querySelectorAll(
        '.dyh-cart-summary__row, .dayvee-cart__summary-line, .dayvee-cart__total'
      )
    ).find(function (row) {
      var labelNode = row.querySelector('span');
      return (
        labelNode &&
        labelNode.textContent.trim().toLowerCase() ===
          String(label).toLowerCase()
      );
    }) || null;
  }

  function patchDrawerSummary(cartData, facts) {
    var drawer = document.querySelector('#CartDrawer');
    if (!drawer) return;

    var summary = drawer.querySelector('[data-dyh-cart-summary]');
    if (!summary) return;

    var subtotal = Number(cartData.total_price || 0);
    var currency = cartData.currency || 'EUR';
    var freeThreshold = 6900;
    var shippingCost = 590;

    var headerRoot = document.querySelector('[data-dayvee-header]');
    if (headerRoot) {
      var configNode = headerRoot.querySelector('[data-dyh-search-config]');
      if (configNode) {
        try {
          var config = JSON.parse(configNode.textContent || '{}');
          if (config.currency) currency = config.currency;
        } catch (error) {}
      }
    }

    /*
     * Die Werte entsprechen den Dayvee-Header-Defaults.
     * Falls sie im Theme-Editor geändert werden, lesen wir sie aus der
     * bereits gerenderten Zusammenfassung ab, soweit möglich.
     */
    var thresholdNote = summary.querySelector(
      '.dyh-cart-summary__note:not(.dyh-cart-summary__note--tax)'
    );

    if (thresholdNote) {
      var thresholdMatch = thresholdNote.textContent.match(
        /(\d[\d.\s]*[,.]\d{2})/
      );
      if (thresholdMatch) {
        var normalized = thresholdMatch[1]
          .replace(/\./g, '')
          .replace(/\s/g, '')
          .replace(',', '.');
        var parsedThreshold = Number(normalized);
        if (Number.isFinite(parsedThreshold)) {
          freeThreshold = Math.round(parsedThreshold * 100);
        }
      }
    }

    var currentShippingRow = findSummaryRow(summary, 'Versand');
    var currentShippingValue = currentShippingRow
      ? currentShippingRow.querySelector('strong')
      : null;

    if (
      currentShippingValue &&
      !/kostenlos|kein versand/i.test(currentShippingValue.textContent)
    ) {
      var shippingMatch = currentShippingValue.textContent.match(
        /(\d[\d.\s]*[,.]\d{2})/
      );
      if (shippingMatch) {
        var normalizedShipping = shippingMatch[1]
          .replace(/\./g, '')
          .replace(/\s/g, '')
          .replace(',', '.');
        var parsedShipping = Number(normalizedShipping);
        if (Number.isFinite(parsedShipping)) {
          shippingCost = Math.round(parsedShipping * 100);
        }
      }
    }

    var appliedShipping =
      facts.hasShippableItems &&
      facts.shippableSubtotal < freeThreshold
        ? shippingCost
        : 0;

    var taxNote = summary.querySelector(
      '.dyh-cart-summary__note--tax'
    );
    setHiddenIfChanged(taxNote, facts.giftCardOnly);

    if (currentShippingValue) {
      if (!facts.hasShippableItems) {
        setTextIfChanged(currentShippingValue, 'Kein Versand');
      } else if (appliedShipping === 0) {
        setTextIfChanged(currentShippingValue, 'Kostenlos ✓');
      } else {
        setTextIfChanged(
          currentShippingValue,
          formatMoney(appliedShipping, currency)
        );
      }
    }

    setHiddenIfChanged(
      thresholdNote,
      !facts.hasShippableItems
    );

    var totalRow = findSummaryRow(summary, 'Gesamtsumme');
    var totalValue = totalRow
      ? totalRow.querySelector('strong')
      : null;

    setTextIfChanged(
      totalValue,
      formatMoney(subtotal + appliedShipping, currency)
    );
  }

  function patchCartPageSummary(cartData, facts) {
    var cart = document.querySelector('.dayvee-cart');
    if (!cart) return;

    var subtotal = Number(cartData.total_price || 0);
    var currency = cartData.currency || 'EUR';

    var taxNote = cart.querySelector('.dayvee-cart__tax-note');
    setHiddenIfChanged(taxNote, facts.giftCardOnly);

    var shippingRow = findSummaryRow(cart, 'Versand');
    var shippingValue = shippingRow
      ? shippingRow.querySelector('strong')
      : null;

    var thresholdNote = cart.querySelector(
      '.dayvee-cart__shipping-note'
    );

    var freeThreshold = 6900;
    if (thresholdNote) {
      var thresholdMatch = thresholdNote.textContent.match(
        /(\d[\d.\s]*[,.]\d{2})/
      );
      if (thresholdMatch) {
        var normalized = thresholdMatch[1]
          .replace(/\./g, '')
          .replace(/\s/g, '')
          .replace(',', '.');
        var parsedThreshold = Number(normalized);
        if (Number.isFinite(parsedThreshold)) {
          freeThreshold = Math.round(parsedThreshold * 100);
        }
      }
    }

    var shippingCost = 590;
    if (
      shippingValue &&
      !/kostenlos|kein versand/i.test(shippingValue.textContent)
    ) {
      var shippingMatch = shippingValue.textContent.match(
        /(\d[\d.\s]*[,.]\d{2})/
      );
      if (shippingMatch) {
        var normalizedShipping = shippingMatch[1]
          .replace(/\./g, '')
          .replace(/\s/g, '')
          .replace(',', '.');
        var parsedShipping = Number(normalizedShipping);
        if (Number.isFinite(parsedShipping)) {
          shippingCost = Math.round(parsedShipping * 100);
        }
      }
    }

    var appliedShipping =
      facts.hasShippableItems &&
      facts.shippableSubtotal < freeThreshold
        ? shippingCost
        : 0;

    if (shippingValue) {
      if (!facts.hasShippableItems) {
        setTextIfChanged(shippingValue, 'Kein Versand');
      } else if (appliedShipping === 0) {
        setTextIfChanged(shippingValue, 'Kostenlos ✓');
      } else {
        setTextIfChanged(
          shippingValue,
          formatMoney(appliedShipping, currency)
        );
      }
    }

    setHiddenIfChanged(
      thresholdNote,
      !facts.hasShippableItems
    );

    var totalRow = findSummaryRow(cart, 'Gesamtsumme');
    var totalValue = totalRow
      ? totalRow.querySelector('strong')
      : null;

    setTextIfChanged(
      totalValue,
      formatMoney(subtotal + appliedShipping, currency)
    );
  }

  function syncCartSummaries() {
    if (cartSummaryBusy) {
      cartSummaryQueued = true;
      return;
    }

    if (
      !document.querySelector('#CartDrawer [data-dyh-cart-summary]') &&
      !document.querySelector('.dayvee-cart')
    ) {
      return;
    }

    cartSummaryBusy = true;

    var root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    fetch(root + 'cart.js', {
      headers: {
        Accept: 'application/json'
      }
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error(
            'Warenkorb konnte nicht geprüft werden.'
          );
        }
        return response.json();
      })
      .then(function (cartData) {
        var facts = getCartFacts(cartData);

        patchDrawerSummary(cartData, facts);
        patchCartPageSummary(cartData, facts);
      })
      .catch(function (error) {
        console.warn(
          '[Dayvee Gutschein Warenkorb]',
          error
        );
      })
      .finally(function () {
        cartSummaryBusy = false;

        if (cartSummaryQueued) {
          cartSummaryQueued = false;
          scheduleCartSummarySync(80);
        }
      });
  }

  function scheduleCartSummarySync(delay) {
    window.clearTimeout(cartSummaryTimer);
    cartSummaryTimer = window.setTimeout(
      syncCartSummaries,
      typeof delay === 'number' ? delay : 120
    );
  }

  function updateGiftCardOnlyCartState() {
    var cart = document.querySelector('.dayvee-cart');
    if (cart) {
      var cartRows = Array.from(
        cart.querySelectorAll('.dayvee-cart__item')
      );
      var cartTaxNote = cart.querySelector(
        '.dayvee-cart__tax-note'
      );

      setHiddenIfChanged(
        cartTaxNote,
        cartRows.length > 0 &&
          cartRows.every(isGiftCardRow)
      );
    }

    var drawer = document.querySelector('#CartDrawer');
    if (drawer) {
      var drawerRows = Array.from(
        drawer.querySelectorAll('.cart-item')
      );
      var giftCardOnly =
        drawerRows.length > 0 &&
        drawerRows.every(isGiftCardRow);

      drawer
        .querySelectorAll(
          '.tax-note, .dyh-cart-summary__note--tax'
        )
        .forEach(function (note) {
          setHiddenIfChanged(note, giftCardOnly);
        });
    }
  }

  function apply() {
    scheduled = false;

    document
      .querySelectorAll(
        '#CartDrawer .cart-item, #main-cart-items .cart-item, .dayvee-cart__item'
      )
      .forEach(lockGiftCardQuantity);

    updateGiftCardOnlyCartState();
    scheduleCartSummarySync();
  }

  function scheduleApply() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(apply);
  }

  function injectStyles() {
    if (document.getElementById('dayvee-gift-card-cart-quantity-style')) return;

    var style = document.createElement('style');
    style.id = 'dayvee-gift-card-cart-quantity-style';
    style.textContent = [
      '.dayvee-gift-card-cart-quantity {',
      '  display: inline-flex;',
      '  align-items: center;',
      '  justify-content: center;',
      '  min-width: 4.6rem;',
      '  min-height: 4.4rem;',
      '  padding: 0.6rem 1rem;',
      '  border: 0.1rem solid rgba(var(--color-foreground), 0.14);',
      '  border-radius: 0.8rem;',
      '  color: rgb(var(--color-foreground));',
      '  background: rgba(var(--color-foreground), 0.025);',
      '  font-size: 1.3rem;',
      '  font-weight: 700;',
      '  line-height: 1;',
      '}',
      '.dayvee-cart__tax-note[hidden],',
      '.dayvee-cart__shipping-note[hidden],',
      '#CartDrawer .tax-note[hidden],',
      '#CartDrawer .dyh-cart-summary__note[hidden] {',
      '  display: none !important;',
      '}',
      '.dayvee-cart__item .dayvee-gift-card-cart-quantity {',
      '  min-width: 132px;',
      '  height: 44px;',
      '  border-color: #cfd4da;',
      '  border-radius: var(--dc-button-radius);',
      '  background: #fff;',
      '  color: var(--dc-text);',
      '  font-size: 15px;',
      '}',
      '@media screen and (max-width: 749px) {',
      '  .dayvee-cart__item .dayvee-gift-card-cart-quantity {',
      '    min-width: 120px;',
      '    height: 42px;',
      '  }',
      '}'
    ].join('\n');

    document.head.appendChild(style);
  }

  function init() {
    if (!initialized) {
      initialized = true;
      injectStyles();

      var observer = new MutationObserver(scheduleApply);
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      document.addEventListener('shopify:section:load', scheduleApply);
    }

    scheduleApply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
